import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CheckCircle2,
  Download,
  FileCheck,
  Loader2,
  Plus,
  Trash2,
  TriangleAlert,
  Upload,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  downloadCampaignQaTemplate,
  diffCampaignQaRun,
  generateCampaignQaCsvFromGoogleAdsExport,
  generateCampaignQaCsvFromRun,
  validateCampaignQaCsv,
  type CampaignQaRunDiffFinding,
  type CampaignQaRunDiffResult,
  type CampaignQaCsvValidateResult,
  type CampaignQaCsvViolation,
  type CampaignQaRuleFinding,
} from '@/services/googleAdsCampaignQaService';
import { keywordResearchHistoryService } from '@/services/keywordResearchHistoryService';
import {
  keywordResearchCampaignHandoffKey,
  type KeywordResearchCampaignHandoff,
} from '@/services/googleAdsCampaignsService';

const QA_CSV_COLUMNS = [
  'row_type',
  'campaign_type',
  'campaign_name',
  'ad_group_name',
  'asset_group_name',
  'scope',
  'entity_name',
  'entity_value',
  'status',
  'match_type',
  'category',
  'primary_for_goal',
  'targeting_mode',
  'attached_campaign_ids',
  'settings',
  'assets',
  'expected_severity',
  'source',
  'notes',
];

const BUILDER_SECTIONS = [
  {
    key: 'setup',
    title: 'Campaign Setup',
    rowTypes: ['campaign', 'location_targeting', 'tracking_template', 'portfolio_bid_strategy'],
    addTypes: ['campaign', 'location_targeting', 'tracking_template'],
  },
  {
    key: 'keywords',
    title: 'Ad Groups & Keywords',
    rowTypes: ['ad_group', 'keyword', 'audience_signal'],
    addTypes: ['ad_group', 'keyword'],
  },
  {
    key: 'ads',
    title: 'Ads & Assets',
    rowTypes: ['rsa', 'rsa_asset', 'asset'],
    addTypes: ['rsa', 'asset'],
  },
  {
    key: 'negatives',
    title: 'Negatives & Safety',
    rowTypes: [
      'negative',
      'content_suitability',
      'pmax_brand_exclusion',
      'pmax_negative_list_attachment',
      'pmax_url_expansion',
    ],
    addTypes: ['negative'],
  },
  {
    key: 'conversions',
    title: 'Conversions',
    rowTypes: ['conversion_action', 'customer_conversion_goal', 'campaign_conversion_goal'],
    addTypes: ['conversion_action'],
  },
];

const BUILDER_FIELDS = [
  'campaign_name',
  'campaign_type',
  'ad_group_name',
  'asset_group_name',
  'scope',
  'entity_name',
  'entity_value',
  'status',
  'match_type',
  'category',
  'primary_for_goal',
  'targeting_mode',
  'attached_campaign_ids',
  'expected_severity',
  'settings',
  'assets',
  'notes',
];

type CampaignQaBuilderRow = Record<string, string>;

interface ParsedCampaignQaCsv {
  headers: string[];
  rows: CampaignQaBuilderRow[];
}

interface GuidedQaFormState {
  campaignGoal: 'LEADS' | 'TRAFFIC_CLICKS';
  biddingStrategy: 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CLICKS' | 'TARGET_CPA';
  campaignName: string;
  serviceName: string;
  adGroupName: string;
  landingPage: string;
  location: string;
  radiusMiles: string;
  dailyBudgetUsd: string;
  conversionTracking: 'yes' | 'no' | 'unknown';
  conversionActionName: string;
  targetCpaUsd: string;
  keywordsText: string;
  negativesText: string;
}

type KeywordResearchImportMode = 'selected' | 'all';
type CampaignQaBuilderTab = 'guided' | 'expert' | 'csv';

interface KeywordResearchAutofill {
  keywords: string[];
  negatives: string[];
  serviceName: string | null;
  adGroupName: string | null;
  landingPage: string | null;
  sourceLabel: string;
}

const DEFAULT_GUIDED_QA_FORM: GuidedQaFormState = {
  campaignGoal: 'LEADS',
  biddingStrategy: 'MAXIMIZE_CONVERSIONS',
  campaignName: '',
  serviceName: '',
  adGroupName: '',
  landingPage: '',
  location: '',
  radiusMiles: '25',
  dailyBudgetUsd: '',
  conversionTracking: 'unknown',
  conversionActionName: '',
  targetCpaUsd: '',
  keywordsText: '',
  negativesText: '',
};

function apiErrorDetail(err: unknown): string {
  const responseDetail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail.trim();
  }
  if (responseDetail) {
    try {
      return JSON.stringify(responseDetail);
    } catch {
      return String(responseDetail);
    }
  }
  const message = (err as { message?: string })?.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : 'No error detail was returned by the API.';
}

function parseCsvRecords(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const nextChar = input[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((record) => record.some((value) => value.trim().length > 0));
}

function parseCampaignQaCsv(csvText: string): ParsedCampaignQaCsv {
  const records = parseCsvRecords(csvText.trim());
  if (records.length === 0) {
    return { headers: QA_CSV_COLUMNS, rows: [] };
  }

  const parsedHeaders = records[0].map((header) => header.trim());
  const headers = [
    ...parsedHeaders,
    ...QA_CSV_COLUMNS.filter((column) => !parsedHeaders.includes(column)),
  ];
  const rows = records.slice(1).map((record) =>
    headers.reduce<CampaignQaBuilderRow>((acc, header, index) => {
      acc[header] = record[index] ?? '';
      return acc;
    }, {}),
  );

  return { headers, rows };
}

function escapeCsvCell(value: string): string {
  if (!/[",\n\r]/.test(value)) {
    return value;
  }
  return `"${value.replaceAll('"', '""')}"`;
}

function serializeCampaignQaCsv(headers: string[], rows: CampaignQaBuilderRow[]): string {
  const csvRows = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) =>
      headers.map((header) => escapeCsvCell(row[header] ?? '')).join(','),
    ),
  ];
  return `${csvRows.join('\n')}\n`;
}

function createBlankCampaignQaRows(): CampaignQaBuilderRow[] {
  const source = 'builder';
  return [
    {
      row_type: 'campaign',
      campaign_type: 'SEARCH',
      campaign_name: '',
      scope: 'campaign',
      entity_name: 'campaign_settings',
      entity_value: 'settings',
      status: 'PAUSED',
      primary_for_goal: 'false',
      settings:
        'goal=LEADS; bidding_strategy=MAXIMIZE_CONVERSIONS; google_search=true; search_partners=false; display=false; location_type=PRESENCE',
      expected_severity: 'BLOCKER',
      source,
    },
    {
      row_type: 'ad_group',
      campaign_type: 'SEARCH',
      campaign_name: '',
      ad_group_name: '',
      scope: 'ad_group',
      entity_name: 'ad_group',
      entity_value: '',
      status: 'ENABLED',
      primary_for_goal: 'false',
      expected_severity: 'BLOCKER',
      source,
    },
    {
      row_type: 'keyword',
      campaign_type: 'SEARCH',
      campaign_name: '',
      ad_group_name: '',
      scope: 'ad_group',
      entity_name: 'keyword',
      entity_value: '',
      status: 'ENABLED',
      match_type: 'PHRASE',
      primary_for_goal: 'false',
      expected_severity: 'BLOCKER',
      source,
    },
    {
      row_type: 'negative',
      campaign_type: 'SEARCH',
      campaign_name: '',
      scope: 'ACCOUNT_SHARED_SET',
      entity_name: 'negative_keyword',
      entity_value: '',
      status: 'ENABLED',
      match_type: 'EXACT',
      primary_for_goal: 'false',
      expected_severity: 'WARNING',
      source,
    },
    {
      row_type: 'rsa',
      campaign_type: 'SEARCH',
      campaign_name: '',
      ad_group_name: '',
      scope: 'ad_group',
      entity_name: 'RSA 1',
      entity_value: 'responsive_search_ad',
      status: 'ENABLED',
      primary_for_goal: 'false',
      assets: 'headlines=; descriptions=',
      expected_severity: 'BLOCKER',
      source,
    },
  ].map((row) => ({ ...Object.fromEntries(QA_CSV_COLUMNS.map((key) => [key, ''])), ...row }));
}

function createBlankRow(rowType: string): CampaignQaBuilderRow {
  const base = Object.fromEntries(QA_CSV_COLUMNS.map((key) => [key, ''])) as CampaignQaBuilderRow;
  const defaults: CampaignQaBuilderRow = {
    row_type: rowType,
    campaign_type: rowType.startsWith('pmax') ? 'PMAX' : 'SEARCH',
    status: 'ENABLED',
    primary_for_goal: 'false',
    expected_severity: rowType === 'campaign' || rowType === 'keyword' || rowType === 'rsa'
      ? 'BLOCKER'
      : 'WARNING',
    source: 'builder',
  };

  if (rowType === 'campaign') {
    Object.assign(defaults, {
      scope: 'campaign',
      entity_name: 'campaign_settings',
      entity_value: 'settings',
      status: 'PAUSED',
    });
  } else if (rowType === 'ad_group') {
    Object.assign(defaults, { scope: 'ad_group', entity_name: 'ad_group' });
  } else if (rowType === 'keyword') {
    Object.assign(defaults, {
      scope: 'ad_group',
      entity_name: 'keyword',
      match_type: 'PHRASE',
    });
  } else if (rowType === 'negative') {
    Object.assign(defaults, {
      scope: 'ACCOUNT_SHARED_SET',
      entity_name: 'negative_keyword',
      match_type: 'EXACT',
    });
  } else if (rowType === 'rsa') {
    Object.assign(defaults, {
      scope: 'ad_group',
      entity_name: 'RSA 1',
      entity_value: 'responsive_search_ad',
      assets: 'headlines=; descriptions=',
      expected_severity: 'BLOCKER',
    });
  } else if (rowType === 'asset') {
    Object.assign(defaults, { scope: 'ad_group', entity_name: 'callout' });
  } else if (rowType === 'location_targeting') {
    Object.assign(defaults, {
      scope: 'campaign',
      entity_name: 'location',
      settings: 'location_type=PRESENCE; radius_miles=',
    });
  } else if (rowType === 'tracking_template') {
    Object.assign(defaults, {
      scope: 'campaign',
      entity_name: 'campaign_tracking_template',
      expected_severity: 'BLOCKER',
    });
  } else if (rowType === 'conversion_action') {
    Object.assign(defaults, {
      scope: 'customer',
      primary_for_goal: 'true',
      expected_severity: 'BLOCKER',
    });
  }

  return { ...base, ...defaults };
}

function fieldLabel(field: string): string {
  return field.replaceAll('_', ' ');
}

function baseQaRow(values: Partial<CampaignQaBuilderRow>): CampaignQaBuilderRow {
  return {
    ...Object.fromEntries(QA_CSV_COLUMNS.map((key) => [key, ''])),
    primary_for_goal: 'false',
    source: 'guided_builder',
    ...values,
  };
}

function splitInputLines(input: string): string[] {
  const seen = new Set<string>();
  return input
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  return values
    .map((value) => value?.trim() ?? '')
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function readJsonFromLocalStorage<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function keywordResearchResultCacheKey(tenantId: string): string {
  return `google_ads_keyword_research_last_result:${tenantId}`;
}

function readCachedSuggestedNegatives(tenantId: string): string[] {
  const cache = readJsonFromLocalStorage<{
    tenant_id?: string;
    result?: {
      negative_keywords?: Array<{ keyword?: string | null }>;
    };
  }>(keywordResearchResultCacheKey(tenantId));
  if (!cache || cache.tenant_id !== tenantId) return [];
  return uniqueStrings(
    cache.result?.negative_keywords?.map((negative) => negative.keyword) ?? [],
  );
}

function readKeywordResearchHandoff(
  tenantId: string,
): KeywordResearchCampaignHandoff | null {
  const handoff = readJsonFromLocalStorage<KeywordResearchCampaignHandoff>(
    keywordResearchCampaignHandoffKey(tenantId),
  );
  if (!handoff || handoff.tenant_id !== tenantId) return null;
  if (!Array.isArray(handoff.selected_keywords)) return null;
  return handoff;
}

async function buildKeywordResearchAutofill(
  tenantId: string,
  mode: KeywordResearchImportMode,
): Promise<KeywordResearchAutofill> {
  const handoff = readKeywordResearchHandoff(tenantId);
  const active = await keywordResearchHistoryService.getActive(tenantId);
  const negatives = readCachedSuggestedNegatives(tenantId);
  const handoffKeywords = uniqueStrings(handoff?.selected_keywords ?? []);
  const handoffDetails = handoff?.selected_keyword_details ?? [];
  const activeClusters = active.run?.clusters ?? [];
  const selectedActiveKeywords = uniqueStrings(
    activeClusters.flatMap((cluster) =>
      cluster.keywords
        .filter((keyword) => keyword.selected_for_campaign)
        .map((keyword) => keyword.keyword),
    ),
  );
  const allActiveKeywords = uniqueStrings(
    activeClusters.flatMap((cluster) =>
      cluster.keywords.map((keyword) => keyword.keyword),
    ),
  );

  let keywords = mode === 'all'
    ? allActiveKeywords
    : handoffKeywords.length > 0
      ? handoffKeywords
      : selectedActiveKeywords;
  let sourceLabel = mode === 'all'
    ? 'all active Keyword Research cluster keywords'
    : handoffKeywords.length > 0
      ? 'saved selected Keyword Research keywords'
      : 'active selected Keyword Research keywords';

  if (mode === 'selected' && keywords.length === 0 && allActiveKeywords.length > 0) {
    keywords = allActiveKeywords;
    sourceLabel = 'all active Keyword Research cluster keywords because no saved selection was found';
  }

  if (keywords.length === 0) {
    throw new Error(
      active.message
        || 'No active Keyword Research keywords were found. Run or save Keyword Research first.',
    );
  }

  const firstHandoffDetail = handoffDetails.find((detail) =>
    keywords.some((keyword) => keyword.toLowerCase() === detail.keyword.toLowerCase()),
  );
  const firstActiveCluster =
    activeClusters.find((cluster) =>
      cluster.keywords.some((keyword) =>
        keywords.some((selected) => selected.toLowerCase() === keyword.keyword.toLowerCase()),
      ),
    ) ?? activeClusters[0];

  return {
    keywords,
    negatives,
    serviceName: firstHandoffDetail?.cluster_name ?? firstActiveCluster?.cluster_name ?? null,
    adGroupName: firstHandoffDetail?.cluster_name ?? firstActiveCluster?.cluster_name ?? null,
    landingPage: firstHandoffDetail?.landing_page ?? firstActiveCluster?.landing_page ?? null,
    sourceLabel,
  };
}

function compactLabel(value: string, fallback: string): string {
  return value.trim().replace(/\s+/g, ' ') || fallback;
}

function limitText(value: string, maxLength: number): string {
  const clean = value.trim().replace(/\s+/g, ' ');
  return clean.length <= maxLength ? clean : clean.slice(0, maxLength).trim();
}

function defaultKeywordsForService(serviceName: string): string[] {
  const service = compactLabel(serviceName, 'lead generation service');
  return [service, `${service} services`, `${service} near me`, `${service} company`];
}

function defaultNegativesForService(serviceName: string): string[] {
  const service = compactLabel(serviceName, '');
  if (!service) {
    return ['free', 'jobs', 'salary', 'course'];
  }
  return [`free ${service}`, `${service} jobs`, `${service} salary`, `${service} course`];
}

function buildGuidedRsaAssets(serviceName: string, location: string): string {
  const service = compactLabel(serviceName, 'Lead Generation');
  const market = compactLabel(location, 'Local');
  const headlines = [
    `${service} Services`,
    `${service} Near You`,
    `Local ${service}`,
    `${market} ${service}`,
    'Get More Qualified Leads',
    'Improve Search Visibility',
    'Book Strategy Call',
    'Marketing That Converts',
    'Grow Search Revenue',
    'Campaign Support',
    'Agile And Co Team',
  ].map((headline) => limitText(headline, 30));
  const descriptions = [
    `Review ${service} campaign setup before launch.`,
    'Check keywords, ads, tracking, and negatives.',
    'Confirm budget, bidding, and location settings.',
  ].map((description) => limitText(description, 90));

  return `headlines=${headlines.join('|')}; descriptions=${descriptions.join('|')}`;
}

function exactKeywordSampleCount(phraseKeywordCount: number): number {
  return phraseKeywordCount > 0 ? Math.max(1, Math.ceil(phraseKeywordCount / 4)) : 0;
}

function generateGuidedCampaignQaRows(form: GuidedQaFormState): CampaignQaBuilderRow[] {
  const serviceName = compactLabel(form.serviceName, 'Lead Gen Service');
  const campaignName = compactLabel(
    form.campaignName,
    `A&C - ${serviceName} - QA Draft`,
  );
  const adGroupName = compactLabel(form.adGroupName, serviceName);
  const location = compactLabel(form.location, 'Primary market');
  const radiusMiles = compactLabel(form.radiusMiles, '25');
  const landingPage = form.landingPage.trim();
  const dailyBudget = form.dailyBudgetUsd.trim();
  const keywords = splitInputLines(form.keywordsText);
  const negatives = splitInputLines(form.negativesText);
  const phraseKeywords = uniqueStrings([
    ...(keywords.length ? keywords : []),
    ...defaultKeywordsForService(serviceName),
  ]).slice(0, Math.max(keywords.length, 4));
  const exactKeywords = phraseKeywords.slice(0, exactKeywordSampleCount(phraseKeywords.length));
  const keywordRows = [
    ...phraseKeywords.map((keyword) =>
      baseQaRow({
        row_type: 'keyword',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        ad_group_name: adGroupName,
        scope: 'ad_group',
        entity_name: 'keyword',
        entity_value: keyword,
        status: 'ENABLED',
        match_type: 'PHRASE',
        expected_severity: 'BLOCKER',
        notes: keywords.length ? 'Guided keyword input' : 'Draft keyword generated from service name',
      }),
    ),
    ...exactKeywords.map((keyword) =>
      baseQaRow({
        row_type: 'keyword',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        ad_group_name: adGroupName,
        scope: 'ad_group',
        entity_name: 'keyword',
        entity_value: keyword,
        status: 'ENABLED',
        match_type: 'EXACT',
        expected_severity: 'BLOCKER',
        notes: 'Guided exact-match QA sample for Art match distribution',
      }),
    ),
  ];
  const negativeTerms = negatives.length ? negatives : defaultNegativesForService(serviceName);
  const tierNegativeTerms = uniqueStrings([
    ...defaultNegativesForService(serviceName),
    ...negativeTerms,
  ]);
  const campaignDirectNegative =
    tierNegativeTerms.find((negative) => /\b(jobs?|careers?|salary)\b/i.test(negative))
    ?? tierNegativeTerms[0]
    ?? 'jobs';
  const adGroupDirectNegative =
    tierNegativeTerms.find((negative) => /\b(course|training|free|cheap)\b/i.test(negative))
    ?? tierNegativeTerms[1]
    ?? 'course';
  const negativeRows = [
    ...negativeTerms.map((negative) =>
      baseQaRow({
        row_type: 'negative',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        scope: 'ACCOUNT_SHARED_SET',
        entity_name: 'negative_keyword',
        entity_value: negative,
        status: 'ENABLED',
        match_type: 'EXACT',
        expected_severity: 'WARNING',
        settings: 'tier=account_shared',
        notes: negatives.length ? 'Guided negative input' : 'Draft non-buyer negative generated from service name',
      }),
    ),
    baseQaRow({
      row_type: 'negative',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      scope: 'CAMPAIGN_DIRECT',
      entity_name: 'negative_keyword',
      entity_value: campaignDirectNegative,
      status: 'ENABLED',
      match_type: 'EXACT',
      attached_campaign_ids: 'needs_review',
      expected_severity: 'WARNING',
      settings: 'tier=campaign_direct',
      notes: 'Guided campaign-level negative tier evidence',
    }),
    baseQaRow({
      row_type: 'negative',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      ad_group_name: adGroupName,
      scope: 'AD_GROUP_DIRECT',
      entity_name: 'negative_keyword',
      entity_value: adGroupDirectNegative,
      status: 'ENABLED',
      match_type: 'EXACT',
      expected_severity: 'WARNING',
      settings: 'tier=ad_group_direct',
      notes: 'Guided ad-group negative tier evidence',
    }),
  ];

  const campaignSettings = [
    `goal=${form.campaignGoal}`,
    'daily_budget_formula=guided_daily_budget_input',
    dailyBudget ? `daily_budget_usd=${dailyBudget}` : 'daily_budget_usd=needs_review',
    `bidding_strategy=${form.biddingStrategy}`,
    form.biddingStrategy === 'TARGET_CPA'
      ? `target_cpa_usd=${form.targetCpaUsd.trim() || 'needs_review'}`
      : '',
    'google_search=true',
    'search_partners=false',
    'display=false',
    'location_type=PRESENCE',
    'parallel_tracking=true',
    'final_url_suffix=utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={creative}&utm_term={keyword}&gclid={gclid}',
    landingPage ? `final_url=${landingPage}` : 'final_url=needs_review',
  ].filter(Boolean).join('; ');

  const rows: CampaignQaBuilderRow[] = [
    baseQaRow({
      row_type: 'campaign',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      scope: 'campaign',
      entity_name: 'campaign_settings',
      entity_value: 'settings',
      status: 'PAUSED',
      settings: campaignSettings,
      expected_severity: 'BLOCKER',
      notes: 'Generated from guided Campaign QA questions',
    }),
    baseQaRow({
      row_type: 'location_targeting',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      scope: 'campaign',
      entity_name: location,
      entity_value: 'location',
      status: 'ENABLED',
      settings: `location_type=PRESENCE; geometry=radius; radius_miles=${radiusMiles}; location=${location}; language=en`,
      expected_severity: 'WARNING',
    }),
    baseQaRow({
      row_type: 'tracking_template',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      scope: 'campaign',
      entity_name: 'campaign_tracking_template',
      entity_value: '{lpurl}?utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={creative}&utm_term={keyword}&gclid={gclid}',
      status: 'ENABLED',
      settings: 'scope=campaign; gclid_required=true; parallel_tracking=true',
      expected_severity: 'BLOCKER',
    }),
    baseQaRow({
      row_type: 'ad_group',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      ad_group_name: adGroupName,
      scope: 'ad_group',
      entity_name: 'ad_group',
      entity_value: adGroupName,
      status: 'ENABLED',
      settings: [
        landingPage ? `landing_page=${landingPage}` : 'landing_page=needs_review',
        `theme=${serviceName}`,
      ].join('; '),
      expected_severity: 'BLOCKER',
    }),
    ...keywordRows,
    ...negativeRows,
    baseQaRow({
      row_type: 'content_suitability',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      scope: 'campaign',
      entity_name: 'placement_exclusion_list',
      entity_value: 'brand_safety',
      status: 'ENABLED',
      attached_campaign_ids: 'needs_review',
      settings: 'tier=content_suitability; excluded_topics=sensitive_social_issues|tragedy_conflict; excluded_content_types=live_streaming|embedded_videos',
      expected_severity: 'WARNING',
      notes: 'Guided content suitability evidence for QA validation',
    }),
    baseQaRow({
      row_type: 'rsa',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      ad_group_name: adGroupName,
      scope: 'ad_group',
      entity_name: 'RSA 1',
      entity_value: 'responsive_search_ad',
      status: 'ENABLED',
      settings: 'ad_strength_min=GOOD; min_headlines=11; min_descriptions=3',
      assets: buildGuidedRsaAssets(serviceName, location),
      expected_severity: 'BLOCKER',
      notes: 'Draft RSA assets generated for QA validation',
    }),
    baseQaRow({
      row_type: 'asset',
      campaign_type: 'SEARCH',
      campaign_name: campaignName,
      ad_group_name: adGroupName,
      scope: 'ad_group',
      entity_name: 'callout',
      entity_value: limitText(`${serviceName} Experts`, 25),
      status: 'ENABLED',
      settings: 'asset_type=CALLOUT; max_chars=25; warning_threshold=23',
      expected_severity: 'WARNING',
    }),
  ];

  if (form.campaignGoal === 'LEADS') {
    const conversionName = compactLabel(form.conversionActionName, 'Lead Form Submit');
    const hasConfirmedConversion = form.conversionTracking === 'yes';
    const conversionStatus = hasConfirmedConversion ? 'ENABLED' : 'PAUSED';
    const conversionSettings = hasConfirmedConversion
      ? 'origin=ADVERTISER_EDITABLE; hidden=false; biddable=true'
      : 'origin=NEEDS_REVIEW; hidden=false; biddable=false';
    const conversionNote = form.conversionTracking === 'yes'
      ? 'Qualified primary conversion confirmed in guided questions'
      : 'Lead-gen campaigns need a primary biddable conversion before launch';

    rows.push(
      baseQaRow({
        row_type: 'conversion_action',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        scope: 'customer',
        entity_name: conversionName,
        entity_value: conversionName,
        status: conversionStatus,
        category: 'SUBMIT_LEAD_FORM',
        primary_for_goal: 'true',
        settings: conversionSettings,
        expected_severity: 'BLOCKER',
        notes: conversionNote,
      }),
      baseQaRow({
        row_type: 'customer_conversion_goal',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        scope: 'customer',
        entity_name: 'SUBMIT_LEAD_FORM',
        entity_value: conversionName,
        status: conversionStatus,
        category: 'SUBMIT_LEAD_FORM',
        primary_for_goal: 'true',
        settings: 'included_in_account_level_goals=true',
        expected_severity: 'BLOCKER',
        notes: conversionNote,
      }),
      baseQaRow({
        row_type: 'campaign_conversion_goal',
        campaign_type: 'SEARCH',
        campaign_name: campaignName,
        scope: 'campaign',
        entity_name: 'SUBMIT_LEAD_FORM',
        entity_value: conversionName,
        status: conversionStatus,
        category: 'SUBMIT_LEAD_FORM',
        primary_for_goal: 'true',
        attached_campaign_ids: 'needs_review',
        settings: 'campaign_override=true; enabled_campaign=true',
        expected_severity: 'BLOCKER',
        notes: conversionNote,
      }),
    );
  }

  return rows;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function severityBadge(severity: string) {
  const className =
    severity === 'BLOCKER'
      ? 'border-destructive/30 bg-destructive/5 text-destructive'
      : severity === 'HIGH' || severity === 'WARNING'
        ? 'border-coral/30 bg-coral-light text-coral-dark'
        : 'border-border bg-warm-bg text-muted-foreground';
  return (
    <Badge variant="outline" className={className}>
      {severity}
    </Badge>
  );
}

function metricCard(label: string, value: string, icon?: JSX.Element) {
  return (
    <div className="rounded border border-border bg-warm-bg p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ResultSummary({ result }: { result: CampaignQaCsvValidateResult }) {
  const rowTypeCounts = useMemo(
    () => Object.entries(result.summary.row_type_counts),
    [result.summary.row_type_counts],
  );
  const verdictClassName =
    result.rule_summary.verdict === 'BLOCKED'
      ? 'border-destructive/30 bg-destructive/5 text-destructive'
      : result.rule_summary.verdict === 'PASS_WITH_WARNINGS'
        ? 'border-coral/30 bg-coral-light text-coral-dark'
        : 'border-teal/30 bg-teal-light text-teal-dark';
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {result.is_valid ? (
          <Badge
            variant="outline"
            className="border-teal/30 bg-teal-light text-teal-dark"
          >
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Structurally valid
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-destructive/30 bg-destructive/5 text-destructive"
          >
            <TriangleAlert className="mr-1 h-3 w-3" />
            Blocked
          </Badge>
        )}
        <span className="text-sm text-muted-foreground">
          Validation only. No Google Ads writes happen here.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={verdictClassName}>
          Rule verdict: {result.rule_summary.verdict}
        </Badge>
        <span className="text-sm text-muted-foreground">
          Business rules check conversion setup, networks, tracking, negatives,
          RSA assets, bidding, and PMax safeguards.
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {metricCard(
          'Rows',
          String(result.summary.rows_total),
          <FileCheck className="h-3 w-3" />,
        )}
        {metricCard('Blockers', String(result.summary.blockers))}
        {metricCard('Warnings', String(result.summary.warnings))}
        {metricCard('Info', String(result.summary.infos))}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {metricCard('Rule blockers', String(result.rule_summary.blockers))}
        {metricCard('Rule warnings', String(result.rule_summary.warnings))}
        {metricCard('Rule info', String(result.rule_summary.infos))}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium">Row Types</h3>
        <div className="flex flex-wrap gap-2">
          {rowTypeCounts.map(([rowType, count]) => (
            <Badge
              key={rowType}
              variant="outline"
              className="border-border bg-background text-foreground"
            >
              {rowType}: {count}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function ViolationsTable({
  violations,
}: {
  violations: CampaignQaCsvViolation[];
}) {
  if (violations.length === 0) {
    return (
      <div className="rounded border border-teal/30 bg-teal-light/40 p-4 text-sm text-teal-dark">
        No structural blockers or warnings were found.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Line</TableHead>
            <TableHead className="w-32">Severity</TableHead>
            <TableHead className="w-40">Field</TableHead>
            <TableHead className="w-52">Code</TableHead>
            <TableHead>Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {violations.map((violation, index) => (
            <TableRow key={`${violation.code}-${violation.line_number}-${index}`}>
              <TableCell className="tabular-nums">
                {violation.line_number ?? '—'}
              </TableCell>
              <TableCell>{severityBadge(violation.severity)}</TableCell>
              <TableCell className="font-mono text-xs">
                {violation.field}
              </TableCell>
              <TableCell className="font-mono text-xs">
                {violation.code}
              </TableCell>
              <TableCell className="min-w-80 text-sm">
                {violation.message}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RuleFindingsTable({
  findings,
}: {
  findings: CampaignQaRuleFinding[];
}) {
  if (findings.length === 0) {
    return (
      <div className="rounded border border-teal/30 bg-teal-light/40 p-4 text-sm text-teal-dark">
        No business-rule findings were returned.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Line</TableHead>
            <TableHead className="w-32">Severity</TableHead>
            <TableHead className="w-36">Category</TableHead>
            <TableHead className="w-52">Code</TableHead>
            <TableHead>Message</TableHead>
            <TableHead className="min-w-48">Expected</TableHead>
            <TableHead className="min-w-48">Actual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {findings.map((finding, index) => (
            <TableRow key={`${finding.code}-${finding.line_number}-${index}`}>
              <TableCell className="tabular-nums">
                {finding.line_number ?? '—'}
              </TableCell>
              <TableCell>{severityBadge(finding.severity)}</TableCell>
              <TableCell className="capitalize">
                {finding.category.replaceAll('_', ' ')}
              </TableCell>
              <TableCell className="font-mono text-xs">{finding.code}</TableCell>
              <TableCell className="min-w-80 text-sm">
                {finding.message}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {finding.expected ?? '—'}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {finding.actual ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RunDiffSummary({ result }: { result: CampaignQaRunDiffResult }) {
  const verdictClassName =
    result.summary.verdict === 'BLOCKED'
      ? 'border-destructive/30 bg-destructive/5 text-destructive'
      : result.summary.verdict === 'PASS_WITH_WARNINGS'
        ? 'border-coral/30 bg-coral-light text-coral-dark'
        : 'border-teal/30 bg-teal-light text-teal-dark';
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={verdictClassName}>
          Run diff: {result.summary.verdict}
        </Badge>
        {result.summary.readback_checked_at && (
          <span className="text-sm text-muted-foreground">
            Read-back checked {new Date(result.summary.readback_checked_at).toLocaleString()}
          </span>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {metricCard('Diff blockers', String(result.summary.blockers))}
        {metricCard('Diff warnings', String(result.summary.warnings))}
        {metricCard('Diff info', String(result.summary.infos))}
      </div>
      <div className="rounded border border-border bg-warm-bg p-3 text-sm">
        <div className="font-medium">Compared Campaigns</div>
        <div className="mt-1 text-muted-foreground">
          {result.summary.compared_campaign_names.length
            ? result.summary.compared_campaign_names.join(', ')
            : 'No matching campaign rows were compared.'}
        </div>
        {result.campaign_resource_name && (
          <div className="mt-2 font-mono text-xs text-muted-foreground">
            {result.campaign_resource_name}
          </div>
        )}
      </div>
    </div>
  );
}

function RunDiffFindingsTable({
  findings,
}: {
  findings: CampaignQaRunDiffFinding[];
}) {
  if (findings.length === 0) {
    return (
      <div className="rounded border border-teal/30 bg-teal-light/40 p-4 text-sm text-teal-dark">
        CSV expectations match the stored campaign run read-back.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Line</TableHead>
            <TableHead className="w-32">Severity</TableHead>
            <TableHead className="w-36">Category</TableHead>
            <TableHead className="w-52">Code</TableHead>
            <TableHead>Message</TableHead>
            <TableHead className="min-w-48">Expected</TableHead>
            <TableHead className="min-w-48">Actual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {findings.map((finding, index) => (
            <TableRow key={`${finding.code}-${finding.line_number}-${index}`}>
              <TableCell className="tabular-nums">
                {finding.line_number ?? '—'}
              </TableCell>
              <TableCell>{severityBadge(finding.severity)}</TableCell>
              <TableCell className="capitalize">
                {finding.category.replaceAll('_', ' ')}
              </TableCell>
              <TableCell className="font-mono text-xs">{finding.code}</TableCell>
              <TableCell className="min-w-80 text-sm">
                {finding.message}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {finding.expected ?? '—'}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {finding.actual ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ChoiceButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function GuidedCampaignQaBuilder({
  form,
  rowCount,
  isKeywordResearchImporting,
  keywordResearchImportMode,
  keywordResearchSummary,
  onChange,
  onGenerate,
  onUseKeywordResearch,
}: {
  form: GuidedQaFormState;
  rowCount: number;
  isKeywordResearchImporting: boolean;
  keywordResearchImportMode: KeywordResearchImportMode | undefined;
  keywordResearchSummary: string | null;
  onChange: (nextForm: GuidedQaFormState) => void;
  onGenerate: () => void;
  onUseKeywordResearch: (mode: KeywordResearchImportMode) => void;
}) {
  function updateField<K extends keyof GuidedQaFormState>(
    field: K,
    value: GuidedQaFormState[K],
  ) {
    onChange({ ...form, [field]: value });
  }

  function setGoal(goal: GuidedQaFormState['campaignGoal']) {
    onChange({
      ...form,
      campaignGoal: goal,
      biddingStrategy: goal === 'TRAFFIC_CLICKS'
        ? 'MAXIMIZE_CLICKS'
        : 'MAXIMIZE_CONVERSIONS',
    });
  }

  return (
    <div className="space-y-5">
      <div className="rounded border border-border bg-warm-bg p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Guided QA Builder</h3>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Answer the campaign questions. The app will fill the expert rows
              and CSV automatically, then the same validation rules will run.
            </p>
          </div>
          <Badge variant="outline" className="bg-background">
            Current draft: {rowCount} row{rowCount === 1 ? '' : 's'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Campaign goal</Label>
          <div className="flex flex-wrap gap-2">
            <ChoiceButton
              active={form.campaignGoal === 'LEADS'}
              onClick={() => setGoal('LEADS')}
            >
              Leads / ROI
            </ChoiceButton>
            <ChoiceButton
              active={form.campaignGoal === 'TRAFFIC_CLICKS'}
              onClick={() => setGoal('TRAFFIC_CLICKS')}
            >
              Traffic / clicks
            </ChoiceButton>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Bidding strategy</Label>
          <div className="flex flex-wrap gap-2">
            <ChoiceButton
              active={form.biddingStrategy === 'MAXIMIZE_CONVERSIONS'}
              onClick={() => updateField('biddingStrategy', 'MAXIMIZE_CONVERSIONS')}
            >
              Max conversions
            </ChoiceButton>
            <ChoiceButton
              active={form.biddingStrategy === 'MAXIMIZE_CLICKS'}
              onClick={() => updateField('biddingStrategy', 'MAXIMIZE_CLICKS')}
            >
              Max clicks
            </ChoiceButton>
            <ChoiceButton
              active={form.biddingStrategy === 'TARGET_CPA'}
              onClick={() => updateField('biddingStrategy', 'TARGET_CPA')}
            >
              Target CPA
            </ChoiceButton>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-service">Service or niche</Label>
          <Input
            id="campaign-qa-guided-service"
            value={form.serviceName}
            onChange={(event) => updateField('serviceName', event.target.value)}
            placeholder="SEO services, plumbing, remodeling"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-campaign">Campaign name</Label>
          <Input
            id="campaign-qa-guided-campaign"
            value={form.campaignName}
            onChange={(event) => updateField('campaignName', event.target.value)}
            placeholder="Leave blank to auto-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-ad-group">Ad group name</Label>
          <Input
            id="campaign-qa-guided-ad-group"
            value={form.adGroupName}
            onChange={(event) => updateField('adGroupName', event.target.value)}
            placeholder="Leave blank to use the service name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-landing">Landing page</Label>
          <Input
            id="campaign-qa-guided-landing"
            value={form.landingPage}
            onChange={(event) => updateField('landingPage', event.target.value)}
            placeholder="https://example.com/service"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-location">Target location</Label>
          <Input
            id="campaign-qa-guided-location"
            value={form.location}
            onChange={(event) => updateField('location', event.target.value)}
            placeholder="St. Louis, Missouri"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="campaign-qa-guided-radius">Radius miles</Label>
            <Input
              id="campaign-qa-guided-radius"
              value={form.radiusMiles}
              onChange={(event) => updateField('radiusMiles', event.target.value)}
              inputMode="decimal"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="campaign-qa-guided-budget">Daily budget</Label>
            <Input
              id="campaign-qa-guided-budget"
              value={form.dailyBudgetUsd}
              onChange={(event) => updateField('dailyBudgetUsd', event.target.value)}
              placeholder="100"
              inputMode="decimal"
            />
          </div>
        </div>

        {form.biddingStrategy === 'TARGET_CPA' && (
          <div className="space-y-2">
            <Label htmlFor="campaign-qa-guided-target-cpa">Target CPA</Label>
            <Input
              id="campaign-qa-guided-target-cpa"
              value={form.targetCpaUsd}
              onChange={(event) => updateField('targetCpaUsd', event.target.value)}
              placeholder="125"
              inputMode="decimal"
            />
          </div>
        )}

        <div className="space-y-2 md:col-span-2">
          <Label>Conversion tracking</Label>
          <div className="flex flex-wrap gap-2">
            <ChoiceButton
              active={form.conversionTracking === 'yes'}
              onClick={() => updateField('conversionTracking', 'yes')}
            >
              Primary conversion exists
            </ChoiceButton>
            <ChoiceButton
              active={form.conversionTracking === 'no'}
              onClick={() => updateField('conversionTracking', 'no')}
            >
              Not configured
            </ChoiceButton>
            <ChoiceButton
              active={form.conversionTracking === 'unknown'}
              onClick={() => updateField('conversionTracking', 'unknown')}
            >
              Not sure
            </ChoiceButton>
          </div>
        </div>

        {form.campaignGoal === 'LEADS' && (
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="campaign-qa-guided-conversion">
              Conversion action name
            </Label>
            <Input
              id="campaign-qa-guided-conversion"
              value={form.conversionActionName}
              onChange={(event) => updateField('conversionActionName', event.target.value)}
              placeholder="Lead Form Submit, Calls from ads, Booked Appointment"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-keywords">
            Keywords, one per line
          </Label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onUseKeywordResearch('selected')}
              disabled={isKeywordResearchImporting}
            >
              {isKeywordResearchImporting && keywordResearchImportMode === 'selected' ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Use selected research
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onUseKeywordResearch('all')}
              disabled={isKeywordResearchImporting}
            >
              {isKeywordResearchImporting && keywordResearchImportMode === 'all' ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Use all research
            </Button>
          </div>
          {keywordResearchSummary && (
            <p className="text-xs text-muted-foreground">{keywordResearchSummary}</p>
          )}
          <Textarea
            id="campaign-qa-guided-keywords"
            value={form.keywordsText}
            onChange={(event) => updateField('keywordsText', event.target.value)}
            placeholder={'seo services\nseo agency near me'}
            className="min-h-32"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-qa-guided-negatives">
            Negative keywords, one per line
          </Label>
          <Textarea
            id="campaign-qa-guided-negatives"
            value={form.negativesText}
            onChange={(event) => updateField('negativesText', event.target.value)}
            placeholder={'free\njobs\nsalary'}
            className="min-h-32"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-border p-4">
        <div className="text-sm text-muted-foreground">
          This creates a QA draft only. It does not write to Google Ads.
        </div>
        <Button type="button" onClick={onGenerate}>
          <Wand2 className="mr-2 h-4 w-4" />
          Generate QA Draft
        </Button>
      </div>
    </div>
  );
}

function CampaignQaExpertBuilder({
  parsed,
  onStartBlank,
  onAddRow,
  onUpdateRow,
  onDeleteRow,
}: {
  parsed: ParsedCampaignQaCsv;
  onStartBlank: () => void;
  onAddRow: (rowType: string) => void;
  onUpdateRow: (rowIndex: number, field: string, value: string) => void;
  onDeleteRow: (rowIndex: number) => void;
}) {
  const rowTypeCounts = useMemo(() => {
    return parsed.rows.reduce<Record<string, number>>((acc, row) => {
      const rowType = row.row_type || 'unknown';
      acc[rowType] = (acc[rowType] ?? 0) + 1;
      return acc;
    }, {});
  }, [parsed.rows]);

  const knownRowTypes = new Set(
    BUILDER_SECTIONS.flatMap((section) => section.rowTypes),
  );
  const sectionRows = BUILDER_SECTIONS.map((section) => ({
    ...section,
    rows: parsed.rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => section.rowTypes.includes(row.row_type)),
  }));
  const otherRows = parsed.rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !knownRowTypes.has(row.row_type));

  if (parsed.rows.length === 0) {
    return (
      <div className="rounded border border-border bg-warm-bg p-5">
        <div className="text-sm font-medium">No QA rows loaded</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Fill from a campaign run, import a Google Ads export, upload a QA CSV,
          or start a blank QA draft.
        </div>
        <Button type="button" className="mt-4" onClick={onStartBlank}>
          <Plus className="mr-2 h-4 w-4" />
          Start Blank Draft
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {Object.entries(rowTypeCounts).map(([rowType, count]) => (
            <Badge key={rowType} variant="outline" className="bg-background">
              {rowType}: {count}
            </Badge>
          ))}
        </div>
        <Button type="button" variant="outline" onClick={onStartBlank}>
          <Plus className="mr-2 h-4 w-4" />
          Start Blank
        </Button>
      </div>

      {[...sectionRows, ...(otherRows.length ? [{
        key: 'other',
        title: 'Other Rows',
        rowTypes: [],
        addTypes: [],
        rows: otherRows,
      }] : [])].map((section) => (
        <div key={section.key} className="space-y-3 rounded border border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">{section.title}</h3>
              <p className="text-xs text-muted-foreground">
                {section.rows.length} row{section.rows.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {section.addTypes.map((rowType) => (
                <Button
                  key={rowType}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onAddRow(rowType)}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {rowType.replaceAll('_', ' ')}
                </Button>
              ))}
            </div>
          </div>

          {section.rows.length === 0 ? (
            <div className="rounded border border-dashed border-border bg-warm-bg px-3 py-4 text-sm text-muted-foreground">
              No rows in this section yet.
            </div>
          ) : (
            <div className="space-y-3">
              {section.rows.map(({ row, index }) => (
                <div
                  key={`${row.row_type}-${index}`}
                  className="rounded border border-border bg-background p-3"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-warm-bg">
                        {row.row_type || 'row'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        CSV line {index + 2}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeleteRow(index)}
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {BUILDER_FIELDS.filter((field) => parsed.headers.includes(field)).map((field) => {
                      const isLongField = field === 'settings' || field === 'assets' || field === 'notes';
                      return (
                        <div
                          key={field}
                          className={isLongField ? 'space-y-1.5 md:col-span-2 xl:col-span-3' : 'space-y-1.5'}
                        >
                          <Label
                            htmlFor={`campaign-qa-builder-${index}-${field}`}
                            className="capitalize"
                          >
                            {fieldLabel(field)}
                          </Label>
                          {isLongField ? (
                            <Textarea
                              id={`campaign-qa-builder-${index}-${field}`}
                              value={row[field] ?? ''}
                              onChange={(event) =>
                                onUpdateRow(index, field, event.target.value)
                              }
                              className="min-h-20 font-mono text-xs"
                            />
                          ) : (
                            <Input
                              id={`campaign-qa-builder-${index}-${field}`}
                              value={row[field] ?? ''}
                              onChange={(event) =>
                                onUpdateRow(index, field, event.target.value)
                              }
                              className="text-sm"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function CampaignQaTab({ tenantId }: { tenantId: string }) {
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [googleAdsExportText, setGoogleAdsExportText] = useState('');
  const [googleAdsExportFileName, setGoogleAdsExportFileName] = useState<string | null>(null);
  const [runId, setRunId] = useState('');
  const [guidedForm, setGuidedForm] = useState<GuidedQaFormState>(DEFAULT_GUIDED_QA_FORM);
  const [builderTab, setBuilderTab] = useState<CampaignQaBuilderTab>('guided');
  const [keywordResearchImportSummary, setKeywordResearchImportSummary] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignQaCsvValidateResult | null>(null);
  const [diffResult, setDiffResult] = useState<CampaignQaRunDiffResult | null>(null);

  const validateMutation = useMutation({
    mutationFn: () => validateCampaignQaCsv({ tenantId, csvText }),
    onSuccess: (nextResult) => {
      setResult(nextResult);
      setDiffResult(null);
      if (!nextResult.is_valid) {
        toast.warning(
          `Campaign QA CSV has ${nextResult.summary.blockers} blocker${nextResult.summary.blockers === 1 ? '' : 's'}`,
        );
      } else if (nextResult.rule_summary.verdict === 'BLOCKED') {
        toast.warning(
          `Campaign QA rules found ${nextResult.rule_summary.blockers} blocker${nextResult.rule_summary.blockers === 1 ? '' : 's'}`,
        );
      } else if (nextResult.rule_summary.verdict === 'PASS_WITH_WARNINGS') {
        toast.warning('Campaign QA passed with rule warnings');
      } else {
        toast.success('Campaign QA passed');
      }
    },
    onError: (err: unknown) => {
      toast.error(`Campaign QA validation failed: ${apiErrorDetail(err)}`);
    },
  });

  const diffMutation = useMutation({
    mutationFn: () =>
      diffCampaignQaRun({
        tenantId,
        csvText,
        runId: runId.trim(),
      }),
    onSuccess: (nextResult) => {
      setDiffResult(nextResult);
      if (nextResult.summary.verdict === 'BLOCKED') {
        toast.warning(
          `Campaign QA diff found ${nextResult.summary.blockers} blocker${nextResult.summary.blockers === 1 ? '' : 's'}`,
        );
      } else if (nextResult.summary.verdict === 'PASS_WITH_WARNINGS') {
        toast.warning('Campaign QA diff passed with warnings');
      } else {
        toast.success('Campaign QA diff passed');
      }
    },
    onError: (err: unknown) => {
      toast.error(`Campaign QA diff failed: ${apiErrorDetail(err)}`);
    },
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      generateCampaignQaCsvFromRun({
        tenantId,
        runId: runId.trim(),
      }),
    onSuccess: (generated) => {
      setCsvText(generated.csv_text);
      setFileName(generated.file_name);
      setResult(null);
      setDiffResult(null);
      toast.success(
        `Filled Campaign QA CSV from ${generated.campaign_names.length} campaign${generated.campaign_names.length === 1 ? '' : 's'}`,
      );
    },
    onError: (err: unknown) => {
      toast.error(`Campaign QA CSV generation failed: ${apiErrorDetail(err)}`);
    },
  });

  const templateMutation = useMutation({
    mutationFn: () => downloadCampaignQaTemplate(tenantId),
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
      toast.success('Downloaded Campaign QA CSV template');
    },
    onError: (err: unknown) => {
      toast.error(`Template download failed: ${apiErrorDetail(err)}`);
    },
  });

  const googleAdsExportMutation = useMutation({
    mutationFn: () =>
      generateCampaignQaCsvFromGoogleAdsExport({
        tenantId,
        csvText: googleAdsExportText,
        fileName: googleAdsExportFileName,
      }),
    onSuccess: (generated) => {
      setCsvText(generated.csv_text);
      setFileName(generated.file_name);
      setResult(null);
      setDiffResult(null);
      toast.success(
        `Filled Campaign QA CSV from Google Ads export with ${generated.rows_total} row${generated.rows_total === 1 ? '' : 's'}`,
      );
    },
    onError: (err: unknown) => {
      toast.error(`Google Ads export import failed: ${apiErrorDetail(err)}`);
    },
  });

  const keywordResearchImportMutation = useMutation({
    mutationFn: (mode: KeywordResearchImportMode) =>
      buildKeywordResearchAutofill(tenantId, mode),
    onSuccess: (autofill) => {
      setGuidedForm((prev) => ({
        ...prev,
        serviceName: prev.serviceName || autofill.serviceName || '',
        adGroupName: prev.adGroupName || autofill.adGroupName || '',
        landingPage: prev.landingPage || autofill.landingPage || '',
        keywordsText: autofill.keywords.join('\n'),
        negativesText: autofill.negatives.length > 0
          ? autofill.negatives.join('\n')
          : prev.negativesText,
      }));
      setKeywordResearchImportSummary(
        `${autofill.keywords.length} keyword${autofill.keywords.length === 1 ? '' : 's'} from ${autofill.sourceLabel}; ${autofill.negatives.length} suggested negative${autofill.negatives.length === 1 ? '' : 's'} loaded.`,
      );
      setResult(null);
      setDiffResult(null);
      toast.success(
        `Loaded ${autofill.keywords.length} Keyword Research keyword${autofill.keywords.length === 1 ? '' : 's'}`,
      );
      if (autofill.negatives.length === 0) {
        toast.info('No cached suggested negatives found in this browser.');
      }
    },
    onError: (err: unknown) => {
      toast.error(`Could not use Keyword Research: ${apiErrorDetail(err)}`);
    },
  });

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setFileName(file.name);
    setCsvText(text);
    setResult(null);
    setDiffResult(null);
  }

  async function handleGoogleAdsExportFileChange(
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setGoogleAdsExportFileName(file.name);
    setGoogleAdsExportText(text);
    setResult(null);
    setDiffResult(null);
  }

  const canValidate = csvText.trim().length > 0 && !validateMutation.isPending;
  const canDiff =
    csvText.trim().length > 0
    && runId.trim().length > 0
    && !diffMutation.isPending;
  const canGenerate = runId.trim().length > 0 && !generateMutation.isPending;
  const canGenerateFromGoogleAdsExport =
    googleAdsExportText.trim().length > 0 && !googleAdsExportMutation.isPending;
  const parsedBuilderCsv = useMemo(() => parseCampaignQaCsv(csvText), [csvText]);

  function rewriteBuilderRows(
    updater: (rows: CampaignQaBuilderRow[]) => CampaignQaBuilderRow[],
    nextHeaders = parsedBuilderCsv.headers,
  ) {
    const nextRows = updater(parsedBuilderCsv.rows);
    setCsvText(serializeCampaignQaCsv(nextHeaders, nextRows));
    setResult(null);
    setDiffResult(null);
  }

  function handleStartBlankDraft() {
    setCsvText(serializeCampaignQaCsv(QA_CSV_COLUMNS, createBlankCampaignQaRows()));
    setFileName('campaign-qa-builder-draft.csv');
    setResult(null);
    setDiffResult(null);
    toast.success('Started a blank Campaign QA draft');
  }

  function handleGenerateGuidedDraft() {
    const rows = generateGuidedCampaignQaRows(guidedForm);
    setCsvText(serializeCampaignQaCsv(QA_CSV_COLUMNS, rows));
    setFileName('campaign-qa-guided-draft.csv');
    setResult(null);
    setDiffResult(null);
    setBuilderTab('expert');
    toast.success(
      `Generated Campaign QA draft with ${rows.length} row${rows.length === 1 ? '' : 's'}`,
    );
  }

  function handleAddBuilderRow(rowType: string) {
    rewriteBuilderRows((rows) => [...rows, createBlankRow(rowType)]);
  }

  function handleUpdateBuilderRow(
    rowIndex: number,
    field: string,
    value: string,
  ) {
    rewriteBuilderRows((rows) =>
      rows.map((row, index) =>
        index === rowIndex ? { ...row, [field]: value } : row,
      ),
    );
  }

  function handleDeleteBuilderRow(rowIndex: number) {
    rewriteBuilderRows((rows) => rows.filter((_, index) => index !== rowIndex));
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-end gap-4">
            <Badge variant="outline" className="bg-warm-bg text-muted-foreground">
              View only
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="campaign-qa-csv-file">Campaign QA CSV</Label>
              <input
                id="campaign-qa-csv-file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="block w-full rounded border border-border bg-background px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-warm-bg file:px-3 file:py-1.5 file:text-sm file:font-medium"
              />
              {fileName && (
                <p className="text-xs text-muted-foreground">
                  Selected: {fileName}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => templateMutation.mutate()}
                disabled={templateMutation.isPending}
              >
                {templateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download Template
              </Button>
              <Button
                type="button"
                onClick={() => validateMutation.mutate()}
                disabled={!canValidate}
              >
                {validateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Validate CSV
              </Button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="campaign-qa-google-ads-export-file">
                Google Ads export CSV
              </Label>
              <input
                id="campaign-qa-google-ads-export-file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleGoogleAdsExportFileChange}
                className="block w-full rounded border border-border bg-background px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-warm-bg file:px-3 file:py-1.5 file:text-sm file:font-medium"
              />
              {googleAdsExportFileName && (
                <p className="text-xs text-muted-foreground">
                  Selected: {googleAdsExportFileName}
                </p>
              )}
            </div>
            <Button
              type="button"
              onClick={() => googleAdsExportMutation.mutate()}
              disabled={!canGenerateFromGoogleAdsExport}
            >
              {googleAdsExportMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              Fill CSV from Export
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="campaign-qa-run-id">Campaign Run ID</Label>
              <Input
                id="campaign-qa-run-id"
                value={runId}
                onChange={(event) => {
                  setRunId(event.target.value);
                  setDiffResult(null);
                }}
                placeholder="Paste a successful campaign run ID"
                className="font-mono text-xs"
              />
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Button
                type="button"
                onClick={() => generateMutation.mutate()}
                disabled={!canGenerate}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Wand2 className="mr-2 h-4 w-4" />
                )}
                Fill CSV from Run
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => diffMutation.mutate()}
                disabled={!canDiff}
              >
                {diffMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileCheck className="mr-2 h-4 w-4" />
                )}
                Compare to Run
              </Button>
            </div>
          </div>

          <Tabs
            value={builderTab}
            onValueChange={(value) => setBuilderTab(value as CampaignQaBuilderTab)}
            className="space-y-4"
          >
            <TabsList>
              <TabsTrigger value="guided">Guided Builder</TabsTrigger>
              <TabsTrigger value="expert">Expert Builder</TabsTrigger>
              <TabsTrigger value="csv">Advanced CSV</TabsTrigger>
            </TabsList>
            <TabsContent value="guided" className="mt-0">
              <GuidedCampaignQaBuilder
                form={guidedForm}
                rowCount={parsedBuilderCsv.rows.length}
                isKeywordResearchImporting={keywordResearchImportMutation.isPending}
                keywordResearchImportMode={keywordResearchImportMutation.variables}
                keywordResearchSummary={keywordResearchImportSummary}
                onChange={setGuidedForm}
                onGenerate={handleGenerateGuidedDraft}
                onUseKeywordResearch={(mode) => keywordResearchImportMutation.mutate(mode)}
              />
            </TabsContent>
            <TabsContent value="expert" className="mt-0">
              <CampaignQaExpertBuilder
                parsed={parsedBuilderCsv}
                onStartBlank={handleStartBlankDraft}
                onAddRow={handleAddBuilderRow}
                onUpdateRow={handleUpdateBuilderRow}
                onDeleteRow={handleDeleteBuilderRow}
              />
            </TabsContent>
            <TabsContent value="csv" className="mt-0">
              <div className="space-y-2">
                <Label htmlFor="campaign-qa-csv-text">CSV Preview</Label>
                <Textarea
                  id="campaign-qa-csv-text"
                  value={csvText}
                  onChange={(event) => {
                    setCsvText(event.target.value);
                    setResult(null);
                    setDiffResult(null);
                  }}
                  placeholder="Paste the campaign QA CSV here, or upload a .csv file above."
                  className="min-h-56 font-mono text-xs"
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-4">
          <ResultSummary result={result} />
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Business Rule Findings</h3>
            <RuleFindingsTable findings={result.rule_findings} />
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium">CSV Structural Findings</h3>
            <ViolationsTable violations={result.violations} />
          </div>
        </div>
      )}

      {diffResult && (
        <div className="space-y-4">
          <RunDiffSummary result={diffResult} />
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Run Diff Findings</h3>
            <RunDiffFindingsTable findings={diffResult.findings} />
          </div>
        </div>
      )}
    </div>
  );
}
